
import { User } from "@/models/user";
import { Agent } from "@/models/agent";
import { toast } from "@/components/ui/use-toast";
import { logger } from "@/utils/logger";
import { useNavigate } from "react-router-dom";

interface DownloadFreeAgentProps {
  agent: Agent;
  user: User | null;
  setPurchasing: (value: boolean) => void;
  setError: (value: string | null) => void;
  setPurchaseSuccess: (value: boolean) => void;
  navigate: ReturnType<typeof useNavigate>;
}

/**
 * Handles the free agent download process
 */
export const handleDownloadFreeAgent = async ({
  agent,
  user,
  setPurchasing,
  setError,
  setPurchaseSuccess,
  navigate
}: DownloadFreeAgentProps) => {
  setPurchasing(true);
  setError(null);
  
  try {
    logger.info("Downloading free agent", { agentId: agent.id, userId: user?.id });
    
    // For free agents, we'll simulate a download without making database changes
    toast({
      title: "Download started",
      description: `${agent.name} is being downloaded`,
      variant: "default"
    });

    // If there's a file URL, we can trigger a download
    if (agent.fileUrl) {
      const link = document.createElement('a');
      link.href = agent.fileUrl;
      link.download = `${agent.name.toLowerCase().replace(/\s+/g, '-')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setPurchaseSuccess(true);
      
      setTimeout(() => {
        toast({
          title: "Download complete",
          description: `${agent.name} has been downloaded successfully`,
          variant: "default"
        });
      }, 1500);
    } else {
      // If there's no file URL, redirect to the agent runtime
      setPurchaseSuccess(true);
      toast({
        title: "Agent ready",
        description: "You can now use this agent",
        variant: "default"
      });
      
      // Redirect to agent runtime after a short delay
      setTimeout(() => {
        navigate(`/runtime/${agent.id}`);
      }, 500);
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Failed to download agent";
    logger.error("Error downloading free agent", { error: errorMessage, agentId: agent.id });
    setError(errorMessage);
    toast({
      title: "Download failed",
      description: "There was an error downloading this agent. Please try again.",
      variant: "destructive"
    });
  } finally {
    setPurchasing(false);
  }
};
